import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/marketing/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import { Input } from "@/marketing/components/ui/input";
import { cn } from "@/marketing/lib/utils";
import {
  type InfluencerCampaign,
  InfluencerCampaignTrafficLight,
  TRAFFIC_DOT,
} from "@/marketing/models/influencerCampaign";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import type { CampaignPipelineDefinition } from "@/marketing/models/campaignPipeline";
import { countByMilestone } from "@/marketing/lib/campaignPipeline";
import { formatDate, safeHostname } from "@/marketing/lib/formatters";
import { influencerCampaignAPIClient } from "@/marketing/infrastructure/influencer/campaign/influencerCampaignAPIClient";
import { INFLUENCER_CAMPAIGN_KEYS } from "@/marketing/infrastructure/influencer/campaign/utils/keys";

interface OverviewSubgridProps {
  campaign: InfluencerCampaign;
  pipelineDefinition: CampaignPipelineDefinition;
  influencers: CampaignInfluencer[];
  onOpenBrief: () => void;
}

function SurfaceCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="tw-rounded-lg tw-border tw-border-border tw-bg-card tw-p-4">
      <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
        <h3 className="tw-text-[13px] tw-font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function TargetsCard({
  campaign,
  pipelineDefinition,
  influencers,
}: {
  campaign: InfluencerCampaign;
  pipelineDefinition: CampaignPipelineDefinition;
  influencers: CampaignInfluencer[];
}) {
  const onboarded = countByMilestone(
    pipelineDefinition,
    influencers,
    "seat_filled",
  );
  // Cumulative: content_posted stays on every later stage, so a creator remains counted after Completed (matches the dashboard postedCount).
  const postsLive = countByMilestone(
    pipelineDefinition,
    influencers,
    "content_posted",
  );
  const onboardingTarget = campaign.onboardingTarget ?? 0;
  const postTarget = campaign.postTarget ?? 0;
  const onbPct =
    onboardingTarget > 0 ? Math.round((onboarded / onboardingTarget) * 100) : 0;
  const postPct =
    postTarget > 0 ? Math.round((postsLive / postTarget) * 100) : 0;

  return (
    <SurfaceCard title="Targets vs Actuals">
      <div className="tw-flex tw-flex-col tw-gap-1.5 tw-py-1">
        <div className="tw-flex tw-items-baseline tw-justify-between">
          <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
            Onboarded creators
          </span>
          <span className="tw-text-sm tw-font-semibold tw-tabular-nums">
            {onboarded}{" "}
            <span className="tw-font-normal tw-text-muted-foreground">
              / {onboardingTarget}
            </span>
          </span>
        </div>
        <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-muted">
          <div
            className="tw-h-full tw-rounded-full tw-bg-violet-600"
            style={{ width: `${Math.min(onbPct, 100)}%` }}
          />
        </div>
        <div className="tw-text-[11px] tw-text-muted-foreground">{onbPct}%</div>
      </div>
      <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-1.5 tw-border-t tw-border-dashed tw-border-border tw-pt-3">
        <div className="tw-flex tw-items-baseline tw-justify-between">
          <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
            Posts live
          </span>
          <span className="tw-text-sm tw-font-semibold tw-tabular-nums">
            {postsLive}{" "}
            <span className="tw-font-normal tw-text-muted-foreground">
              / {postTarget}
            </span>
          </span>
        </div>
        <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-muted">
          <div
            className="tw-h-full tw-rounded-full tw-bg-emerald-500"
            style={{ width: `${Math.min(postPct, 100)}%` }}
          />
        </div>
        <div className="tw-text-[11px] tw-text-muted-foreground">{postPct}%</div>
      </div>
    </SurfaceCard>
  );
}

function BriefPeekCard({
  campaign,
  onOpenBrief,
}: {
  campaign: InfluencerCampaign;
  onOpenBrief: () => void;
}) {
  const briefLines = (campaign.brief ?? "")
    .split("\n")
    .filter((line) => line.trim().length > 0);
  return (
    <SurfaceCard
      title="Brief & guardrails"
      action={
        <button
          type="button"
          onClick={onOpenBrief}
          className="tw-text-xs tw-text-blue-600 hover:tw-underline"
        >
          Open brief →
        </button>
      }
    >
      <div className="tw-text-[13px] tw-leading-relaxed tw-text-foreground/80">
        {briefLines.length === 0 ? (
          <p className="tw-text-muted-foreground">No brief yet.</p>
        ) : (
          briefLines.slice(0, 3).map((line, i) => (
            <p
              key={i}
              className={i === 0 ? undefined : "tw-text-muted-foreground"}
            >
              {i === 0 ? line : `· ${line}`}
            </p>
          ))
        )}
      </div>
      {campaign.consideration && (
        <div className="tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-2.5 tw-py-2 tw-text-[12px] tw-text-amber-800">
          <AlertTriangle className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-flex-shrink-0" />
          <div>
            <strong>Consideration:</strong> {campaign.consideration}
          </div>
        </div>
      )}
    </SurfaceCard>
  );
}

function ScheduleCard({ campaign }: { campaign: InfluencerCampaign }) {
  const overdue =
    campaign.postingDueDate &&
    new Date(campaign.postingDueDate).getTime() < Date.now();
  return (
    <SurfaceCard title="Schedule">
      <div className="tw-flex tw-flex-col tw-gap-3 tw-pl-1">
        <Row
          label="Campaign start"
          value={formatDate(campaign.startAt)}
          sub="Onboarding kicked off"
        />
        <Row
          label="Today"
          value={formatDate(new Date().toISOString())}
          sub="Live monitoring"
          tone="violet"
        />
        <Row
          label="Posting deadline"
          value={formatDate(campaign.postingDueDate)}
          sub={overdue ? "Overdue" : "Posts must be live"}
          tone={overdue ? "red" : "default"}
        />
        <Row
          label="Campaign end"
          value={formatDate(campaign.endAt)}
          sub="Final review + payouts"
        />
      </div>
    </SurfaceCard>
  );
}

function Row({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub: string;
  tone?: "default" | "violet" | "red";
}) {
  return (
    <div className="tw-relative tw-pl-4">
      <span
        className={cn(
          "tw-absolute tw-left-0 tw-top-1.5 tw-h-1.5 tw-w-1.5 tw-rounded-full",
          tone === "default" && "tw-bg-muted-foreground/40",
          tone === "violet" && "tw-bg-violet-600 tw-ring-2 tw-ring-violet-100",
          tone === "red" && "tw-bg-red-500",
        )}
      />
      <div className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
        {label}
      </div>
      <div className="tw-text-[13px] tw-font-medium">{value}</div>
      <div
        className={cn(
          "tw-text-[11px]",
          tone === "red" ? "tw-text-red-600" : "tw-text-muted-foreground",
        )}
      >
        {sub}
      </div>
    </div>
  );
}

function HealthCard({ campaign }: { campaign: InfluencerCampaign }) {
  const tl = campaign.trafficLight;
  const label =
    tl === InfluencerCampaignTrafficLight.Red
      ? "At risk"
      : tl === InfluencerCampaignTrafficLight.Yellow
        ? "Watch"
        : tl === InfluencerCampaignTrafficLight.Green
          ? "On track"
          : "Not set";
  return (
    <SurfaceCard title="Health summary">
      <div className="tw-flex tw-items-start tw-gap-3">
        {tl ? (
          <span
            className={cn(
              "tw-mt-1 tw-h-3 tw-w-3 tw-flex-shrink-0 tw-rounded-full",
              TRAFFIC_DOT[tl],
            )}
          />
        ) : (
          <span className="tw-mt-1 tw-h-3 tw-w-3 tw-flex-shrink-0 tw-rounded-full tw-bg-muted" />
        )}
        <div className="tw-text-[13px]">
          <strong className="tw-block">{label}</strong>
          <p className="tw-mt-1 tw-text-muted-foreground">
            {campaign.consideration ?? "No flags raised."}
          </p>
        </div>
      </div>
    </SurfaceCard>
  );
}

function ProductsCard({ campaign }: { campaign: InfluencerCampaign }) {
  const refs = campaign.productRefs ?? [];
  const [open, setOpen] = useState(false);
  return (
    <>
      <SurfaceCard
        title="Product references"
        action={
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-muted-foreground hover:tw-text-foreground"
          >
            <Plus className="tw-h-3 tw-w-3" /> Add link
          </button>
        }
      >
        {refs.length === 0 ? (
          <p className="tw-text-[12px] tw-text-muted-foreground">
            No product references yet.
          </p>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            {refs.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-py-1.5 tw-text-[12px] hover:tw-bg-accent"
              >
                <ExternalLink className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
                <span className="tw-flex-1 tw-truncate">
                  {url.replace(/^https?:\/\//, "")}
                </span>
                <span className="tw-text-[11px] tw-text-muted-foreground">
                  {safeHostname(url)}
                </span>
              </a>
            ))}
          </div>
        )}
      </SurfaceCard>
      <AddProductLinkDialog
        open={open}
        onOpenChange={setOpen}
        campaignId={campaign.campaignId}
        existingRefs={refs}
      />
    </>
  );
}

interface AddProductLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  existingRefs: string[];
}

function AddProductLinkDialog({
  open,
  onOpenChange,
  campaignId,
  existingRefs,
}: AddProductLinkDialogProps) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");

  const mutation = useMutation({
    mutationFn: (newUrl: string) =>
      influencerCampaignAPIClient.updateInfluencerCampaign(campaignId, {
        productRefs: [...existingRefs, newUrl],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.detail(campaignId),
      });
      queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.lists(),
      });
      toast.success("Product link added");
      setUrl("");
      onOpenChange(false);
    },
    onError: () => {
      toast.error("Failed to add product link");
    },
  });

  const trimmed = url.trim();
  const isDuplicate = trimmed.length > 0 && existingRefs.includes(trimmed);
  const canSubmit =
    trimmed.length > 0 && !isDuplicate && !mutation.isPending;

  function handleSubmit() {
    if (!canSubmit) return;
    mutation.mutate(trimmed);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setUrl("");
        onOpenChange(next);
      }}
    >
      <DialogContent className="tw-max-w-sm">
        <DialogHeader>
          <DialogTitle>Add product link</DialogTitle>
        </DialogHeader>
        <div className="tw-py-2">
          <Input
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="https://..."
          />
          {isDuplicate && (
            <p className="tw-mt-1.5 tw-text-xs tw-text-red-600">
              This link is already added.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {mutation.isPending ? "Adding…" : "Add link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OverviewSubgrid({
  campaign,
  pipelineDefinition,
  influencers,
  onOpenBrief,
}: OverviewSubgridProps) {
  return (
    <div className="tw-grid tw-gap-3 tw-px-8 tw-pb-6 tw-pt-4 lg:tw-grid-cols-[2fr_1fr]">
      <div className="tw-flex tw-flex-col tw-gap-3">
        <TargetsCard
          campaign={campaign}
          pipelineDefinition={pipelineDefinition}
          influencers={influencers}
        />
        <BriefPeekCard campaign={campaign} onOpenBrief={onOpenBrief} />
      </div>
      <div className="tw-flex tw-flex-col tw-gap-3">
        <ScheduleCard campaign={campaign} />
        <HealthCard campaign={campaign} />
        <ProductsCard campaign={campaign} />
      </div>
    </div>
  );
}
